import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import type {
  GearCapabilities,
  IconikRole,
  Option,
} from "@cinesys/experience-ui/components";
import CustomerApiService, { ApiError } from "../../CustomerApiService";
import { growl } from "../../host/Toast";

/**
 * Entries source for `@cinesys/experience-ui`'s menu chrome (`components/GearMenu/`, a real
 * mechanical port of Cantemo's own `cs_item_gear` `Menu`/`MenuEntry`/`Gear` — see
 * `docs/decisions.md`) — real iconik custom actions, listed/triggered via `apps/customer-api`'s
 * `cs_item_gear` routes. Follows this app's house `react-query` convention (`CLAUDE.md`'s "Data
 * fetching (react-query)"), same shape as `cs_metadata_viewer/service.ts`: this is a genuinely
 * new file, no Cantemo `cs_item_gear` frontend service to port (Cantemo's own config-driven
 * entries come from Portal DOM-scraping/plugin-config with nothing to adapt from — see
 * `GearMenu/Menu.tsx`'s own header comment). The entries this hook returns flow into the ported
 * chrome through its `Gear` `options` prop, the same `additionalOptions` merge point
 * `GearMenu/Menu.tsx` has — see that file's header comment for the wiring.
 *
 * The iconik token used for every request this makes is resolved server-side, per request, from
 * the caller's own session cookie (`CustomerIconikService.getIconik`, never the admin/master
 * token) — nothing here ever sees or forwards a token itself.
 */

/** Mirrors `@cinesys/iconik`'s `IconikCustomActionContext` (kept as a local literal type rather
 *  than importing the package here — this app doesn't otherwise depend on `@cinesys/iconik`,
 *  which is a backend-only package; see `apps/customer-frontend/CLAUDE.md`). Must stay in sync
 *  with `apps/customer-api/src/routes/cs_item_gear/types.ts`'s `SUPPORTED_CONTEXTS`. */
export type IconikCustomActionContext =
  | "ASSET"
  | "ASSET_SUBCLIP"
  | "COLLECTION"
  | "SAVED_SEARCH"
  | "BULK";

/** Mirrors `apps/customer-api/src/routes/cs_item_gear/types.ts`'s `CustomActionSummary`. */
export interface CustomActionSummary {
  id: string;
  title: string;
  type: "OPEN" | "POST" | "PUBLISH";
}

interface TriggerCustomActionResponse {
  redirectUrl?: string | null;
}

const PREFIX = "/cs_item_gear/actions";

/** `GET /cs_item_gear/actions/:context`. A 404 means the feature is disabled/unconfigured for
 *  this org (`cs_item_gear/index.ts`'s `requireEnabled`) — treated as "no entries," the exact
 *  same honest-empty-menu state `GearMenu.tsx` already renders for zero options, not a new error
 *  UI. Any OTHER failure still propagates so `useQuery`'s `error` is real for a genuine outage. */
export async function listCustomActions(
  context: IconikCustomActionContext
): Promise<CustomActionSummary[]> {
  try {
    return await CustomerApiService.fetch<CustomActionSummary[]>(
      "GET",
      `${PREFIX}/${context}`
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return [];
    }
    throw e;
  }
}

/**
 * `BULK`-context entityId is `ItemBrowserList.tsx`'s own comma-joined bulk-selection string —
 * `"itemId/fileId"` pairs (see that file's `bulkEntityId`), not a single asset id. Splits it back
 * into the real `itemId` array iconik's `BULK` custom-action callback needs (`asset_ids`, per
 * `iconik-docs fetch assets`'s `CustomActionCallbackSchema` — the field is a plain array
 * regardless of context, so `BULK` sends more than one entry where `ASSET` always sent exactly
 * one), dropping any pair with no itemId — a plain, not-yet-imported file has no iconik asset to
 * run a custom action against (menuTypeContext.ts never resolves a `BULK` context for a
 * file-only selection in the first place, but a mixed selection can still include one).
 */
function parseBulkAssetIds(entityId: string): string[] {
  return entityId
    .split(",")
    .map((pair) => pair.split("/")[0])
    .filter(Boolean);
}

/** `POST /cs_item_gear/actions/:context/:actionId/callback`. Which id array gets populated
 *  depends on the action's own context — mirrors `apps/customer-api`'s
 *  `TriggerCustomActionRequest`. */
export function triggerCustomAction(
  context: IconikCustomActionContext,
  actionId: string,
  entityId: string
): Promise<TriggerCustomActionResponse> {
  const body =
    context === "COLLECTION"
      ? { collectionIds: [entityId] }
      : context === "SAVED_SEARCH"
      ? { savedSearchIds: [entityId] }
      : context === "BULK"
      ? { assetIds: parseBulkAssetIds(entityId) }
      : // ASSET / ASSET_SUBCLIP: iconik subclips are their own asset records (same `id` this app
        // already treats as an asset id elsewhere, e.g.
        // `IconikMetadataService.updateAssetMetadataView` for a subclip's own metadata), so
        // `assetIds` is correct for both.
        { assetIds: [entityId] };
  return CustomerApiService.fetch<TriggerCustomActionResponse>(
    "POST",
    `${PREFIX}/${context}/${actionId}/callback`,
    body
  );
}

export const itemGearQueryKeys = {
  actions: (context: string) => ["cs_item_gear", "actions", context] as const,
  capabilities: () => ["cs_item_gear", "capabilities"] as const,
};

/** Mirrors `apps/customer-api/src/services/iconikCapabilities.ts`'s
 *  `GearCapabilitiesResponse`. */
export interface GearCapabilitiesResponse {
  roles: string[];
  isAdmin: boolean;
  settings: {
    enableFaceRecognition: boolean;
    enableAiMetadataFilling: boolean;
    hideFavourites: boolean;
    aiConsentGiven: boolean;
  };
}

const NO_CAPABILITIES: GearCapabilitiesResponse = {
  roles: [],
  isAdmin: false,
  settings: {
    enableFaceRecognition: false,
    enableAiMetadataFilling: false,
    hideFavourites: false,
    aiConsentGiven: false,
  },
};

/** `GET /cs_item_gear/capabilities`. A 404 means the feature is off for this org — same
 *  treatment as `listCustomActions` above, resolving to "no permissions" so every built-in
 *  entry hides rather than rendering and then failing on click. */
export async function getGearCapabilities(): Promise<GearCapabilitiesResponse> {
  try {
    return await CustomerApiService.fetch<GearCapabilitiesResponse>(
      "GET",
      "/cs_item_gear/capabilities"
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return NO_CAPABILITIES;
    }
    throw e;
  }
}

/**
 * The calling user's gear permissions, as the shape `@cinesys/experience-ui`'s descriptors read.
 *
 * Org-wide and user-wide, not per-entity, so one query serves every gear on the page. While it
 * is loading — and if it fails — every built-in entry stays hidden: showing an action the user
 * cannot perform and letting them discover that from a failed request is worse than a menu that
 * fills in a moment later.
 */
export function useGearCapabilities(): GearCapabilities {
  const { data } = useQuery<GearCapabilitiesResponse, Error>(
    itemGearQueryKeys.capabilities(),
    getGearCapabilities,
    { staleTime: 5 * 60_000 }
  );

  return useMemo(
    () => ({
      roles: new Set((data?.roles ?? []) as IconikRole[]),
      isAdmin: data?.isAdmin ?? false,
      settings: data?.settings ?? NO_CAPABILITIES.settings,
    }),
    [data]
  );
}

/**
 * Fetches the real custom actions for one context (cached/shared across every row that opens the
 * same context's gear — custom actions are org-wide, not per-asset, so one list call covers every
 * item on a page) and returns them as `GearMenu.tsx` `Option[]`, ready to hand straight to a
 * `<Gear options={...} />`.
 *
 * Icon: iconik's `CustomActionSchema` has no icon field (`docs/decisions.md`'s 2026-09-03 entry)
 * — `icon` is left `undefined` here on purpose, an explicit placeholder for a future icon rather
 * than an omitted field, since `Option.icon` already exists on the ported chrome's own shape and
 * currently has no reader in `GearMenuHost`'s render either way.
 */
export function useCustomActionOptions(
  context: IconikCustomActionContext | undefined
): Option[] {
  const { data } = useQuery<CustomActionSummary[], Error>(
    itemGearQueryKeys.actions(context ?? ""),
    () => listCustomActions(context as IconikCustomActionContext),
    { enabled: !!context, staleTime: 60_000 }
  );

  const trigger = useMutation<
    TriggerCustomActionResponse,
    Error,
    { actionId: string; entityId: string; title: string }
  >(
    ({ actionId, entityId }) => {
      if (!context) {
        return Promise.reject(new Error("No custom-action context"));
      }
      return triggerCustomAction(context, actionId, entityId);
    },
    {
      // Fire-and-forget feedback on the HTTP response only, not completion/job-status polling —
      // the action itself runs async on iconik's side once this callback returns 200, and whether
      // it actually finished successfully there is a separate concern this hook doesn't track.
      onSuccess: (reply, { title }) => {
        // `OPEN`-type actions reply with a URL to send the user to; `POST`/`PUBLISH` actions
        // generally don't (see docs/decisions.md's 2026-09-01 entry — the three types' exact
        // semantics beyond this are undocumented upstream). Opening it is the only generically
        // correct reaction available without type-specific branching this round's scope doesn't
        // cover. "started" reads oddly for a navigate-away action, so the redirect case gets its
        // own wording.
        if (reply.redirectUrl) {
          growl(`${title} opened`, "success");
          window.open(reply.redirectUrl, "_blank", "noreferrer");
        } else {
          growl(`${title} started`, "success");
        }
      },
      onError: (_error, { title }) => {
        growl(`${title} failed`, "error");
      },
    }
  );

  return useMemo(
    () =>
      (data ?? []).map(
        (action): Option => ({
          key: `custom-action-${action.id}`,
          label: action.title,
          icon: undefined,
          callback: (entityId) => {
            if (entityId) {
              trigger.mutate({
                actionId: action.id,
                entityId,
                title: action.title,
              });
            }
          },
        })
      ),

    // react-query v3 mutation hook instance; re-deriving Option[] on every mutation state change
    // would needlessly re-render every open gear menu mid-click.
    [data]
  );
}
